import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { loginWithPhonePin } from '../services/authService';
import { clearSession, getSession, refreshAccessToken, revokeToken } from '../services/oauth';
import { storageKeys } from '../config/environment';
import { normalizeRoleKey, organizationNeedsRoleChoice } from '../utils/organizationRoleUtils';

const AuthContext = createContext();

const safeParse = (value, fallback = null) => {
    try {
        return value ? JSON.parse(value) : fallback;
    } catch (error) {
        console.error('Erreur de parsing', error);
        return fallback;
    }
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrganization, setSelectedOrganization] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authState, setAuthState] = useState({
        authenticated: false,
        access_token: null,
        refresh_token: null,
        expires_in: null,
        expires_at: null
    });
    const refreshTokenScheduler = useRef(null);
    const navigate = useNavigate();

    /**
     * Restaure la session au démarrage
     */
    useEffect(() => {
        const restoreSession = async () => {
            const session = getSession();
            
            if (!session) {
                setLoading(false);
                return;
            }

            // Vérifier si le token est expiré
            if (session.expires_at && session.expires_at < Date.now()) {
                // Essayer de rafraîchir si on a un refresh_token
                if (session.refresh_token) {
                    try {
                        const newSession = await refreshAccessToken(session.refresh_token);
                        setAuthState({
                            authenticated: true,
                            access_token: newSession.access_token,
                            refresh_token: newSession.refresh_token,
                            expires_in: newSession.expires_in,
                            expires_at: newSession.expires_at
                        });
                    } catch (error) {
                        console.error('Erreur lors de la restauration de la session', error);
                        clearSession();
                        setLoading(false);
                        return;
                    }
                } else {
                    clearSession();
                    setLoading(false);
                    return;
                }
            } else {
                setAuthState({
                    authenticated: true,
                    access_token: session.access_token,
                    refresh_token: session.refresh_token,
                    expires_in: session.expires_in,
                    expires_at: session.expires_at
                });
            }

            // Restaurer les données utilisateur
            const savedUser = safeParse(localStorage.getItem(storageKeys.user));
            const savedOrganizations = safeParse(localStorage.getItem(storageKeys.organizations), []);
            const savedOrganisation = safeParse(localStorage.getItem(storageKeys.organisation));

            if (savedUser) {
                setUser(savedUser);
            }
            if (savedOrganizations?.length) {
                setOrganizations(savedOrganizations);
            }
            if (savedOrganisation) {
                setSelectedOrganization(savedOrganisation);
            }

            setLoading(false);
        };

        restoreSession();
    }, []);

    /**
     * Planifie le rafraîchissement automatique du token
     */
    useEffect(() => {
        if (authState?.expires_in && authState?.refresh_token) {
            const expiresIn = authState.expires_in; // en secondes
            const refreshTime = (expiresIn - 60) * 1000; // Rafraîchir 60 secondes avant expiration

            if (refreshTokenScheduler.current) {
                clearTimeout(refreshTokenScheduler.current);
            }

            refreshTokenScheduler.current = setTimeout(async () => {
                try {
                    const newSession = await refreshAccessToken(authState.refresh_token);
                    setAuthState({
                        authenticated: true,
                        access_token: newSession.access_token,
                        refresh_token: newSession.refresh_token,
                        expires_in: newSession.expires_in,
                        expires_at: newSession.expires_at
                    });
                } catch (error) {
                    console.error('Erreur lors du rafraîchissement automatique', error);
                    // En cas d'erreur, déconnecter l'utilisateur
                    logout();
                }
            }, refreshTime);
        }

        return () => {
            if (refreshTokenScheduler.current) {
                clearTimeout(refreshTokenScheduler.current);
            }
        };
    }, [authState.expires_in, authState.refresh_token]);

    /**
     * Écouter les événements de déconnexion
     */
    useEffect(() => {
        const handleLogout = () => {
            logout();
        };

        window.addEventListener('auth:logout', handleLogout);
        return () => {
            window.removeEventListener('auth:logout', handleLogout);
        };
    }, []);

    const selectOrganization = useCallback((organizationId, role = 'admin', organizationOverride = null) => {
        const normalizedRole = normalizeRoleKey(role) || 'admin';
        
        console.log('[AuthContext.selectOrganization] Appelé avec:', {
            organizationId,
            role,
            normalizedRole,
            hasOverride: !!organizationOverride,
            organizationOverrideData: organizationOverride ? {
                id: organizationOverride.id,
                name: organizationOverride.name,
                role: organizationOverride.role,
                roles: organizationOverride.roles,
                current_user_roles: organizationOverride.current_user_roles
            } : null
        });

        let organization = organizationOverride;
        
        // Si pas d'override, chercher dans le contexte
        if (!organization) {
            organization = organizations.find((org) => org.id === organizationId);
        }
        
        // Si toujours pas trouvé, chercher dans localStorage
        if (!organization) {
            const savedOrganizations = safeParse(localStorage.getItem(storageKeys.organizations), []);
            organization = savedOrganizations.find((org) => org.id === organizationId);
        }
        
        // Si toujours pas trouvé, utiliser l'organisation sélectionnée actuelle
        if (!organization) {
            organization = selectedOrganization;
        }
        
        // Si toujours pas trouvé, chercher dans localStorage directement
        if (!organization) {
            const savedOrganisation = safeParse(localStorage.getItem(storageKeys.organisation));
            if (savedOrganisation && savedOrganisation.id === organizationId) {
                organization = savedOrganisation;
            }
        }

        if (!organization) {
            console.error('[selectOrganization] Organisation introuvable:', {
                organizationId,
                organizationsInContext: organizations.length,
                savedOrganizations: safeParse(localStorage.getItem(storageKeys.organizations), []).length,
                selectedOrganization: selectedOrganization?.id
            });
            throw new Error("Organisation introuvable.");
        }

        // Stocker le rôle sélectionné avec l'organisation pour référence future
        // Important : on stocke le rôle réellement sélectionné (normalisé), pas celui de l'organisation
        const organizationWithSelectedRole = {
            ...organization,
            selectedRole: normalizedRole // Ajouter le rôle sélectionné normalisé comme propriété séparée
        };

        console.log('[AuthContext.selectOrganization] Données finales:', {
            organizationData: {
                id: organization.id,
                name: organization.name,
                role: organization.role, // Le rôle de l'organisation (peut être 'both')
                selectedRole: normalizedRole, // Le rôle réellement sélectionné par l'utilisateur (normalisé)
                roles: organization.roles,
                current_user_roles: organization.current_user_roles
            },
            targetRoute: normalizedRole === 'cashier' ? '/cashier' : '/dashboard',
            willNavigateTo: normalizedRole === 'cashier' ? '/cashier' : '/dashboard'
        });

        setSelectedOrganization(organizationWithSelectedRole);
        localStorage.setItem(storageKeys.organisation, JSON.stringify(organizationWithSelectedRole));
        localStorage.setItem(storageKeys.destination, organization.id);
        // Stocker aussi le rôle sélectionné séparément pour référence facile
        localStorage.setItem(storageKeys.selectedRole || 'netikash_selected_role', normalizedRole);

        // Rediriger selon le rôle : caissier -> /cashier (interface caissier avec sidebar et pages dédiées), admin -> /dashboard
        const targetRoute = normalizedRole === 'cashier' ? '/cashier' : '/dashboard';
        console.log('[AuthContext.selectOrganization] Navigation vers:', targetRoute, '(rôle normalisé:', normalizedRole, ')');
        navigate(targetRoute);
    }, [navigate, organizations, selectedOrganization]);

    const login = async ({ phone, pin, countryCode }) => {
        setLoading(true);
        try {
            const result = await loginWithPhonePin({ phone, pin, countryCode });
            const permissions = result.user.permissions || ['view_dashboard', 'process_payments'];
            const nextUser = { ...result.user, permissions };

            // Récupérer la session après l'authentification
            const session = getSession();
            if (session) {
                setAuthState({
                    authenticated: true,
                    access_token: session.access_token,
                    refresh_token: session.refresh_token,
                    expires_in: session.expires_in,
                    expires_at: session.expires_at
                });
            }

            setUser(nextUser);
            setOrganizations(result.organizations);

            localStorage.setItem(storageKeys.user, JSON.stringify(nextUser));
            localStorage.setItem(storageKeys.organizations, JSON.stringify(result.organizations));

            setLoading(false);

            if (result.organizations.length === 1) {
                const target = result.organizations[0];
                if (organizationNeedsRoleChoice(target)) {
                    return { success: true, organizations: result.organizations, needsRoleChoice: true };
                }
                selectOrganization(target.id, target.role, target);
                return { success: true, autoRedirect: true };
            }

            return { success: true, organizations: result.organizations };
        } catch (error) {
            setLoading(false);
            return { success: false, error: error.message || 'Authentification impossible.' };
        }
    };

    const logout = useCallback(async () => {
        // Annuler le rafraîchissement automatique
        if (refreshTokenScheduler.current) {
            clearTimeout(refreshTokenScheduler.current);
            refreshTokenScheduler.current = null;
        }

        // Révoquer le token si possible
        if (authState.access_token) {
            try {
                await revokeToken(authState.access_token);
            } catch (error) {
                console.error('Erreur lors de la révocation du token', error);
            }
        }

        // Nettoyer la session et le localStorage
        clearSession();
        Object.values(storageKeys).forEach((key) => localStorage.removeItem(key));
        
        // Réinitialiser l'état
        setUser(null);
        setOrganizations([]);
        setSelectedOrganization(null);
        setAuthState({
            authenticated: false,
            access_token: null,
            refresh_token: null,
            expires_in: null,
            expires_at: null
        });

        navigate('/');
    }, [authState.access_token, navigate]);

    const hasPermission = (permission) => {
        if (!user?.permissions) return false;
        return user.permissions.includes(permission);
    };

    const refreshToken = async () => {
        if (authState.refresh_token) {
            try {
                const newSession = await refreshAccessToken(authState.refresh_token);
                setAuthState({
                    authenticated: true,
                    access_token: newSession.access_token,
                    refresh_token: newSession.refresh_token,
                    expires_in: newSession.expires_in,
                    expires_at: newSession.expires_at
                });
                return newSession;
            } catch (error) {
                console.error('Erreur lors du rafraîchissement manuel', error);
                throw error;
            }
        }
        return getSession();
    };

    const value = {
        user,
        loading,
        login,
        logout,
        hasPermission,
        refreshToken,
        isAuthenticated: authState.authenticated && !!getSession(),
        organizations,
        selectedOrganization,
        selectOrganization,
        authState
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const ProtectedRoute = ({ children, requiredPermission }) => {
    const { isAuthenticated, hasPermission, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-500 to-amber-600">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                    <p className="text-white font-semibold">Chargement...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    if (requiredPermission && !hasPermission(requiredPermission)) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100">
                <div className="text-center">
                    <div className="text-6xl mb-4">🚫</div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Accès Refusé</h2>
                    <p className="text-gray-600">Vous n'avez pas la permission d'accéder à cette page.</p>
                </div>
            </div>
        );
    }

    return children;
};